<script setup lang="ts">
// https://github.com/jackocnr/intl-tel-input/blob/master/vue/README.md
// NOTE: typing is not implemented by <IntlTelInput>
// https://github.com/jackocnr/intl-tel-input/issues/1794
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import IntlTelInput from 'intl-tel-input/vueWithUtils';
import 'intl-tel-input/styles';

const props = defineProps<{
  name: string
  label?: string
  error?: string
}>();

const emit = defineEmits<{
  focus: []
  blur: []
}>();

// NOTE: <IntlTelInput> errored during SSR
// Add this & <ClientOnly> to prevent that
const isMounted = useMounted();

const {
  value: modelValue,
  errorMessage: internalErrorMessage,
  setErrors,
  handleChange,
  handleReset,
} = useField<string | undefined>(
  props.name,
  (value: string | undefined) => {
    if (!value) return `agent.form.error.phone`;
    if (typeof value !== 'string') return `agent.form.error.phone`;
    return true;
  },
);

const { t } = useI18n();
const errorMessageWithValidation = computed(() => {
  if (props.error) return props.error;
  if (internalErrorMessage.value) return t(internalErrorMessage.value);
  return undefined;
});
const hasError = computed(() => !!props.error || !!internalErrorMessage.value);

const input = shallowRef<HTMLInputElement>();

async function onUpdate(value: unknown) {
  if (!value || typeof value !== 'string') {
    setErrors('core.form.error.mandatory_field');
    return;
  }
  handleChange(value);
  return modelValue.value = value;
}

// https://github.com/jackocnr/intl-tel-input?tab=readme-ov-file#initialisation-options
const options = {
  initialCountry: 'fr',
  countryOrder: ['es', 'fr', 'pt', 'it', 'de', 'gb'],
  containerClass: 'w-full',
  // Need to set up this flag to false because the country-list popup is displayed below the dialog on mobile screen
  // so we use the dropdown instead
  useFullscreenPopup: false,
};

// Since <IntlTelInput> in an uncontrolled input
// we want to keep in sync with parent v-model
// Since as for now we only need to reset & redraw the value and the inputfield
// Handle only empty string
watch(modelValue, () => {
  if (modelValue.value) return;
  // see top comment about <IntlTelInput> typing
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  input.value?.instance?.setNumber('');
  handleReset();
});
</script>

<template>
  <div
    class="group"
  >
    <IadLabel
      :html-for="name"
      :text="label"
      :error="errorMessageWithValidation"
    />
    <ClientOnly>
      <IntlTelInput
        v-if="isMounted"
        ref="input"
        :value="modelValue"
        class="border focus:border-secondary outline-none
          placeholder:text-neutral placeholder:font-medium
          block w-full px-s py-xs rounded-md peer"
        :class="{
          'border-neutral-30': !hasError,
          'border-error': hasError,
        }"
        :options
        :input-props=" {
          id: name,
          onFocus: () => emit('focus'),
          onBlur: () => emit('blur'),
        }"
        @change-number="onUpdate"
      />
    </ClientOnly>
    <input
      v-if="!isMounted"
      :id="name"
      :name
      readonly
      class="border focus:border-secondary outline-none
          placeholder:text-neutral placeholder:font-medium
          block w-full px-s py-xs rounded-md"
      :class="{
        'border-neutral': !hasError,
        'border-error': hasError,
      }"
    >
    <IadInputError :error="errorMessageWithValidation" />
  </div>
</template>
